import { Toast } from '@base-ui/react/toast'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import ErrorRounded from '@mui/icons-material/ErrorRounded'
import InfoRounded from '@mui/icons-material/InfoRounded'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import defaultI18n from 'i18next'
import { useContext } from 'react'
import { I18nContext, useTranslation } from 'react-i18next'

import { toastManager } from './notify'
import * as Styled from './toaster.styled'

import type { JSX } from 'react'

const icons: Record<string, JSX.Element> = {
  success: <CheckCircleRounded style={{ color: 'var(--Foreground-Secondary, #28B800)', fontSize: 20 }} />,
  error: <ErrorRounded style={{ color: 'var(--Border-Critical, #cb101d)', fontSize: 20 }} />,
  info: <InfoRounded style={{ color: 'var(--foreground-primary, #5A58F2)', fontSize: 20 }} />,
  warning: <WarningAmberRounded style={{ color: 'var(--Foreground-Warning, #E08600)', fontSize: 20 }} />,
  loading: (
    <Styled.Spinner>
      <span>
        {Array.from({ length: 12 }, (_, index) => (
          <i key={index} style={{ animationDelay: `${-1.2 + index * 0.1}s`, rotate: `${index * 30}deg` }} />
        ))}
      </span>
    </Styled.Spinner>
  ),
}

const ToastList = (): JSX.Element[] => {
  const i18n = useContext(I18nContext)?.i18n ?? defaultI18n
  const { t } = useTranslation(undefined, { i18n, useSuspense: false })
  return Toast.useToastManager().toasts.map((toast) => (
    <Styled.Card
      key={toast.id}
      toast={toast}
      swipeDirection={['up', 'right']}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
        }
      }}
    >
      <Styled.Content>
        <Styled.Icon aria-hidden>{icons[toast.type ?? '']}</Styled.Icon>
        <Styled.Body>
          <Styled.Title />
          <Styled.Description />
        </Styled.Body>
      </Styled.Content>
      {toast.type !== 'loading' && (
        <Styled.Close aria-label={t('libs_toast_close')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Styled.Close>
      )}
    </Styled.Card>
  ))
}

export const Toaster = (): JSX.Element => (
  <Toast.Provider toastManager={toastManager} timeout={4000}>
    <Styled.Viewport>
      <ToastList />
    </Styled.Viewport>
  </Toast.Provider>
)
